/**
 * Centralized constants for TakeFav
 * All application constants, configuration values, and magic numbers must be defined here
 */

// Import centralized environment configuration
import { getBaseAPI } from '../utils/environment'

// API Configuration
export const API_CONFIG = {
  BASE_URL: getBaseAPI(),
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  CACHE_TTL: 5 * 60 * 1000, // 5 minutes
} as const

// API Endpoints - Use relative paths so they work with useApi composable
export const API_ENDPOINTS = {
  PRODUCTS: {
    LIST: '/api/products',
    GET_BY_SLUG: '/api/products/by-slug',
    CREATE: '/api/products',
    UPDATE: '/api/products',
    DELETE: '/api/products',
  },
  CATEGORIES: {
    LIST: '/api/categories',
    GET_BY_SLUG: '/api/categories/by-slug',
  },
  ARTICLES: {
    LIST: '/api/articles',
    GET_BY_SLUG: '/api/articles/by-slug',
  },
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    LOGOUT: '/api/auth/logout',
    REFRESH: '/api/auth/refresh',
    VERIFY: '/api/auth/verify',
  },
  TF: {
    ITEMS: '/api/tf/items',
    reactions: (itemId: string) => `/api/tf/items/${itemId}/reactions`,
    comments: (itemId: string) => `/api/tf/items/${itemId}/comments`,
    report: (itemId: string) => `/api/tf/items/${itemId}/report`,
  },
} as const

export const REACTION_OPTIONS = [
  { key: 'like', icon: '👍', label: 'Like' },
  { key: 'love', icon: '😍', label: 'Love' },
  { key: 'wholesome', icon: '😇', label: 'Wholesome' },
  { key: 'funny', icon: '😂', label: 'Funny' },
  { key: 'wow', icon: '😮', label: 'Wow' },
  { key: 'sad', icon: '😢', label: 'Sad' },
  { key: 'support', icon: '💪', label: 'Support' },
] as const

export const REPORT_REASONS = [
  { value: 'spam', label: 'Spam or self promotion' },
  { value: 'inappropriate', label: 'Inappropriate or adult content' },
  { value: 'misleading', label: 'Misleading or scam' },
  { value: 'dangerous', label: 'Dangerous or harmful content' },
  { value: 'harassment', label: 'Harassment or hate speech' },
  { value: 'other', label: 'Other' },
] as const

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  MAX_PAGES_DISPLAYED: 7,
} as const

// Image Configuration
export const IMAGE_CONFIG = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_FORMATS: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  THUMBNAIL_WIDTH: 300,
  THUMBNAIL_HEIGHT: 300,
  HERO_WIDTH: 1200,
  HERO_HEIGHT: 600,
  CARD_WIDTH: 400,
  CARD_HEIGHT: 300,
  MOBILE_WIDTH: 768,
  MOBILE_HEIGHT: 400,
} as const

// Form Validation
export const VALIDATION = {
  MIN_PASSWORD_LENGTH: 8,
  MAX_PASSWORD_LENGTH: 128,
  MIN_USERNAME_LENGTH: 3,
  MAX_USERNAME_LENGTH: 50,
  MIN_PRODUCT_NAME_LENGTH: 3,
  MAX_PRODUCT_NAME_LENGTH: 200,
  MIN_DESCRIPTION_LENGTH: 10,
  MAX_DESCRIPTION_LENGTH: 2000,
  MIN_ARTICLE_TITLE_LENGTH: 10,
  MAX_ARTICLE_TITLE_LENGTH: 100,
  MIN_ARTICLE_CONTENT_LENGTH: 100,
  MAX_ARTICLE_CONTENT_LENGTH: 10000,
} as const

// Cache Keys
export const CACHE_KEYS = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  ARTICLES: 'articles',
  USER_PROFILE: 'user_profile',
  USER_FAVORITES: 'user_favorites',
  SEARCH_RESULTS: 'search_results',
  CATEGORY_PRODUCTS: 'category_products',
} as const

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
  LANGUAGE: 'language',
  RECENT_SEARCHES: 'recent_searches',
  CART_ITEMS: 'cart_items',
} as const

// Route Names
export const ROUTES = {
  HOME: '/',
  PRODUCTS: '/products',
  CATEGORIES: '/categories',
  ARTICLES: '/articles',
  ABOUT: '/about',
  CONTACT: '/contact',
  LOGIN: '/login',
  REGISTER: '/register',
  PROFILE: '/profile',
  FAVORITES: '/favorites',
  ADMIN: '/admin',
  CRM: '/crm',
} as const

// Breakpoints
export const BREAKPOINTS = {
  XS: 0,
  SM: 576,
  MD: 768,
  LG: 992,
  XL: 1200,
  XXL: 1400,
} as const

// Z-Index Values
export const Z_INDEX = {
  DROPDOWN: 1000,
  STICKY: 1020,
  FIXED: 1030,
  MODAL_BACKDROP: 1040,
  MODAL: 1050,
  POPOVER: 1060,
  TOOLTIP: 1070,
  TOAST: 1080,
} as const

// Animation Durations
export const ANIMATION = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
  VERY_SLOW: 1000,
} as const

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network connection error. Please check your internet connection.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  AUTHENTICATION_ERROR: 'Please log in to continue.',
  AUTHORIZATION_ERROR: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
  RATE_LIMIT_EXCEEDED: 'Too many requests. Please wait a moment and try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_URL: 'Please enter a valid URL.',
  PASSWORD_TOO_SHORT: `Password must be at least ${VALIDATION.MIN_PASSWORD_LENGTH} characters long.`,
  PASSWORD_TOO_LONG: `Password must be no more than ${VALIDATION.MAX_PASSWORD_LENGTH} characters long.`,
  USERNAME_TOO_SHORT: `Username must be at least ${VALIDATION.MIN_USERNAME_LENGTH} characters long.`,
  USERNAME_TOO_LONG: `Username must be no more than ${VALIDATION.MAX_USERNAME_LENGTH} characters long.`,
} as const

// Success Messages
export const SUCCESS_MESSAGES = {
  PRODUCT_CREATED: 'Product created successfully!',
  PRODUCT_UPDATED: 'Product updated successfully!',
  PRODUCT_DELETED: 'Product deleted successfully!',
  CATEGORY_CREATED: 'Category created successfully!',
  CATEGORY_UPDATED: 'Category updated successfully!',
  CATEGORY_DELETED: 'Category deleted successfully!',
  ARTICLE_CREATED: 'Article created successfully!',
  ARTICLE_UPDATED: 'Article updated successfully!',
  ARTICLE_DELETED: 'Article deleted successfully!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  PASSWORD_CHANGED: 'Password changed successfully!',
  LOGIN_SUCCESS: 'Login successful!',
  REGISTRATION_SUCCESS: 'Registration successful!',
  LOGOUT_SUCCESS: 'Logout successful!',
  FAVORITE_ADDED: 'Added to favorites!',
  FAVORITE_REMOVED: 'Removed from favorites!',
} as const

// Loading Messages
export const LOADING_MESSAGES = {
  LOADING_PRODUCTS: 'Loading products...',
  LOADING_CATEGORIES: 'Loading categories...',
  LOADING_ARTICLES: '',
  LOADING_USER_DATA: 'Loading user data...',
  SAVING_DATA: 'Saving data...',
  UPLOADING_IMAGE: 'Uploading image...',
  PROCESSING_REQUEST: 'Processing request...',
  GENERATING_CONTENT: 'Generating content...',
} as const

// Affiliate Configuration
export const AFFILIATE_CONFIG = {
  AMAZON_TAG: 'dalins209-20',
  EBAY_CAMPID: '5339111533',
  ALIEXPRESS_AFF: 'takefav',
  COMMISSION_RATE: 0.05, // 5%
  MIN_COMMISSION: 0.5, // $0.50
} as const

// SEO Configuration
export const SEO_CONFIG = {
  DEFAULT_TITLE: 'TakeFav - Find Your Favorite Products',
  DEFAULT_DESCRIPTION:
    'Discover amazing products from top retailers with expert reviews and recommendations.',
  DEFAULT_KEYWORDS: 'products, reviews, recommendations, shopping, deals',
  MAX_TITLE_LENGTH: 60,
  MAX_DESCRIPTION_LENGTH: 160,
  MAX_KEYWORDS_COUNT: 10,
} as const

// Performance Configuration
export const PERFORMANCE = {
  DEBOUNCE_DELAY: 300,
  THROTTLE_DELAY: 100,
  LAZY_LOAD_OFFSET: 100,
  INFINITE_SCROLL_THRESHOLD: 0.8,
  IMAGE_LAZY_LOADING: true,
  COMPONENT_LAZY_LOADING: true,
} as const

// Security Configuration
export const SECURITY = {
  PASSWORD_MIN_ENTROPY: 50,
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours
  REFRESH_TOKEN_TIMEOUT: 7 * 24 * 60 * 60 * 1000, // 7 days
  MAX_LOGIN_ATTEMPTS: 5,
  LOCKOUT_DURATION: 15 * 60 * 1000, // 15 minutes
  CSRF_TOKEN_EXPIRY: 60 * 60 * 1000, // 1 hour
} as const

// Export all constants as a single object for easy access
export const CONSTANTS = {
  API_CONFIG,
  PAGINATION,
  IMAGE_CONFIG,
  VALIDATION,
  CACHE_KEYS,
  STORAGE_KEYS,
  ROUTES,
  BREAKPOINTS,
  Z_INDEX,
  ANIMATION,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  LOADING_MESSAGES,
  AFFILIATE_CONFIG,
  SEO_CONFIG,
  PERFORMANCE,
  SECURITY,
  REACTION_OPTIONS,
  REPORT_REASONS,
} as const
